/*
 * ////////////////////////////////////////////////////////////////////////////////
 * //
 * // This software system consists of computer software and documentation.
 * // It contains trade secrets and confidential information which are proprietary
 * // to Everi Games Inc.  Its use or disclosure in whole or in part without
 * // the express written permission of Everi Games Inc. is prohibited.
 * //
 * // This software system is also an unpublished work protected under the copyright
 * // laws of the United States of America.
 * //
 * // Copyright © 2018 Everi Games Inc.  All Rights Reserved
 * //
 * ////////////////////////////////////////////////////////////////////////////////
 */
import LocalizationComponent from '../../../Localization/LocalizationComponent';
import BetManager from '../../../Playbar/BetManager';
import ComponentBase from '../../ComponentBase';
import CLog, { LogTypes } from '../../ComponentLogger';
import Observable from '../../Observable/Observable';
import InjectableComponent from '../ComponentReference/InjectableComponent';
import ObservableDoubleComponent from './ObservableDoubleComponent';
import UpdateableComponentBase from './UpdateableComponentBase';
import { CurrencyUtil } from '../../CurrencyUtil';

/** @hidden */
// tslint:disable-next-line:typedef
const property = cc._decorator.property;

/**
 * Implementation of [[UpdateableComponentBase]] for updating [[cc.Label]]s or
 * [[cc.RichText]] when a target observable changes.
 */
@cc._decorator.ccclass
export default class UpdateableLabelComponent extends UpdateableComponentBase<cc.Label | cc.RichText, any>
{
    /** The Label or RichText to update when the target observable changes. */
    protected component: cc.Label | cc.RichText = null;
    public get Component(): cc.Label | cc.RichText { return this.component; }

    /** The search value to find in Label or RichText template for where to insert the observed value. */
    @property({ visible: true, readonly: true })
    private readonly _templateReplaceable: string = '{value}';

    /** The template to use for the Label or RichText when updating. */
    @property({ visible: true, multiline: true })
    private _templateLiteral: string = this._templateReplaceable;
    public set TemplateLiteral(value: string) { this._templateLiteral = value; }

    /** Should the updated contents of the Label or RichText be localized. */
    @property({ visible: true })
    private _isLocalized: boolean = false;
    /** Mutator for if the Label or RichText updates should be localized. */
    public set IsLocalized(value: boolean) { this._isLocalized = value; this.updateUI(); }

    /** Should this component be hidden when the observable is currently at its minimum value. */
    @property({ visible: true })
    private _hideOnMinValue: boolean = false;

    /** If the observable value is a number with a decimal, should the decimal portion of the value be removed. */
    @property({ visible: true })
    private _hideDecimalComponent: boolean = false;
    /** Mutator for whether or not the decimal portion of an observed value should be hidden. */
    public set HideDecimalComponent(value: boolean) { this._hideDecimalComponent = value; this.updateUI(); }

    /** @hidden */
    private _betManagerRef: InjectableComponent<BetManager> = new InjectableComponent<BetManager>();

    /** Length of last set label value. */
    protected stringLengthObservable: Observable<number> = new Observable<number>(0);
    /** Public accessor to last set label value. */
    public get StringLengthObservable(): Observable<number> { return this.stringLengthObservable; }

    /** @hidden */
    protected setContexts(): void
    {
        this._betManagerRef.SetContext(BetManager);
        this.localRef.SetContext(LocalizationComponent);
    }

    /** @hidden */
    protected onLoad(): void
    {
        super.onLoad();

        this.component = ComponentBase.GetFirstComponent<cc.Label>(this.node, cc.Label, cc.RichText);

        if (!this.component)
        {
            CLog.error("No label component. Disabled.", this, LogTypes.Initialization);
            this.enabled = false;
            return;
        }
    }

    /** @hidden */
    protected start(): void
    {
        super.start();

        this.updateUI();
    }

    /** @hidden */
    protected updateUI = (event?: cc.Event.EventCustom): void =>
    {
        if (!this.observable || !this.component) { return; }

        if (this.observable.HasValue())
        {
            this.setLabelString(this.tryLocalize(this.observable.Value));
        }
    }

    private setLabelString = (value: string): string =>
    {
        if (this._hideOnMinValue && this.observable.Value === this.observable.MinValue)
        {
            this.component.enabled = false;
            return;
        }
        else
        {
            this.component.enabled = true;
        }

        this.stringLengthObservable.Value = value.toString().length;

        if (this._templateLiteral && this._templateLiteral !== this._templateReplaceable)
        {
            value = this._templateLiteral
                .split(this._templateReplaceable)
                .join(value);
        }

        if (value === this.component.string) { return; }
        this.component.string = value;
    }

    private formatNumber(num: number): string
    {
        return num.toLocaleString(this.localRef.Value.CultureKey,  { maximumFractionDigits: 0 });
    }

    private tryLocalize = (value: any): string =>
    {
        if (isNaN(value))
        {
            if (value.length) { this.stringLengthObservable.Value = value.length; }
            return value;
        }

        let numberValue: number = value.valueOf();

        // Check for display modificaiton function and modify if available.
        let observableDouble: ObservableDoubleComponent = (this.observable as ObservableDoubleComponent);
        if (observableDouble.NumberDisplayMod)
        {
            numberValue = observableDouble.NumberDisplayMod(numberValue);
        }

        if (!this._isLocalized)
        {
            value = this.formatNumber(this._hideDecimalComponent ? Math.floor(numberValue) : numberValue);
        }
        else
        {
            value = CurrencyUtil.GetCurrencyString(this.localRef.Value, numberValue);
        }
        return value;
    }

    /** If the target observable is value restricted, bumps it to the next highest valid value. */
    public IncreaseValue(): void
    {
        if (!this.observable) { return; }

        this.observable.SetNextHigherValidValue();
    }

    /** If the target observable is value restricted, bumps it to the next lowest valid value. */
    public DecreaseValue(): void
    {
        if (!this.observable) { return; }

        this.observable.SetNextLowerValidValue();
    }
}
